import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ApiResponse,
  ICreateOperatorProps,
  DeleteOperator,
  Pagination,
  Operator,
  IOperatorListItem,
  AllOperatorsListApiResponse,
} from "../sagas/operatorSaga";
import { IFetchOperators } from "@/services/operator/operator.type";

export interface OperatorState {
  operators: ApiResponse;
  loading: boolean;
  singleLoading: boolean;
  error: boolean;
  operator: Operator;
  operatorsList: AllOperatorsListApiResponse;
  shouldRefetchOperators: boolean;
  lastFetchedProviderId: string | null;
  message?: string;
}

export const initialState: OperatorState = {
  operators: {
    data: [],
    pagination: {
      totalRecords: 0,
      currentPage: 0,
      totalPages: 0,
      nextPage: 0,
      previousPage: 0,
    },
    success: false,
    message: "",
  },
  loading: false,
  singleLoading: false,
  error: false,
  operator: {
    name: "",
    operatorId: "",
    sourceId: "",
    providerId: "",
    subapplicationId: "",
    outsideReferenceId: "",
    isDeleted: false,
    createdOn: "",
    updatedOn: "",
    provider: {
      providerId: "",
      name: ""
    },
    subapplication: {
      id: "",
      name: ""
    },
  },
  operatorsList: {
    success: false,
    message: "",
    data: [],
    pagination: {
      totalRecords: 0,
      currentPage: 0,
      totalPages: 0,
      nextPage: 0,
      previousPage: 0,
    },
  },
  shouldRefetchOperators: false,
  lastFetchedProviderId: null
};

const operatorSlice = createSlice({
  name: "operator",
  initialState,
  reducers: {
    getOperators: (state, action: PayloadAction<IFetchOperators>) => {
      state.loading = true;
    },
    getOperatorsSuccess: (state, action: PayloadAction<ApiResponse>) => {
      state.loading = false;
      state.operators = { ...action.payload };
    },
    getOperatorsFailure: (state) => {
      state.loading = false;
      state.error = true;
    },
    getOperator: (state, action: PayloadAction<string>) => {
      state.singleLoading = true;
    },
    getOperatorSuccess: (state, action: PayloadAction<Operator>) => {
      state.singleLoading = false;
      state.operator = { ...action.payload }; // Avoid direct assignment, create a new object
    },
    getOperatorFailure: (state) => {
      state.singleLoading = false;
      state.error = true;
    },
    //List API 
    getOperatorsList: (state, action: PayloadAction<IFetchOperators>) => {
      state.loading = true;
      state.error = false;
      state.message = ""
    },
    getOperatorsListSuccess: (state, action: PayloadAction<AllOperatorsListApiResponse & { providerId: string }>) => {
      state.loading = false;
      const { providerId, data, pagination, message, success } = action.payload;

      const isSameProvider = state.lastFetchedProviderId === providerId;
      const isNextPage = pagination?.currentPage > 1 && isSameProvider;
      state.error = false;
      state.message = action.payload.message;
      state.operatorsList = {
        success,
        message,
        pagination,
        data: isNextPage
          ? [...state.operatorsList.data, ...data]
          : [...data],
      };

      state.lastFetchedProviderId = providerId; // ✅ track providerId used
    },
    getOperatorsListFailure: (state, action: any) => {
      state.loading = false;
      state.error = true;
      state.message = action.payload?.messageDetails?.length ? action.payload?.messageDetails[0]?.message : action.payload?.message
    },
    createOperator: (state, action: PayloadAction<ICreateOperatorProps>) => {
      state.loading = true;
    },
    createOperatorSuccess: (state, action: PayloadAction<Operator>) => {
      state.loading = false;
      state.operator = { ...action.payload }; // Avoid direct assignment, create a new object
      state.shouldRefetchOperators = true;

    },
    createOperatorFailure: (state) => {
      state.loading = false;
      state.error = false;
    },
    updateOperator: (state, action: PayloadAction<ICreateOperatorProps>) => {
      state.loading = true;
    },
    updateOperatorSuccess: (state, action: PayloadAction<Operator>) => {
      state.loading = false;
      state.operator = { ...action.payload }; // Avoid direct assignment, create a new object
      state.shouldRefetchOperators = true;
    },
    updateOperatorFailure: (state) => {
      state.loading = false;
      state.error = false;
    },
    deleteOperator: (state, action: PayloadAction<DeleteOperator>) => {
      state.loading = true;
    },
    deleteOperatorSuccess: (state, action: PayloadAction<Operator>) => {
      state.loading = false;
      state.shouldRefetchOperators = true;
    },
    deleteOperatorFailure: (state) => {
      state.loading = false;
      state.error = false;
    },
    activateOperator: (state, action: PayloadAction<Operator>) => {
      state.loading = true;
    },
    activateOperatorSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.message = action.payload?.message || 'Operator activated successfully.';
      state.shouldRefetchOperators = true;
    },
    activateOperatorFailure: (state, action: any) => {
      state.loading = false;
      state.error = true;
      state.message = action.payload?.message || 'Failed to activate operator.';
    },
    setShouldRefetchOperators: (state, action: PayloadAction<boolean>) => {
      state.shouldRefetchOperators = action.payload;
    },
    clearOperatorError: (state) => {
      state.error = false;
      state.message = ''; // Reset message to empty string as per initial state
    },
    // New reducer to handle the duplicate call scenario
    getOperatorsNoChange: (state) => {
      state.loading = false;
      state.message = 'No new data to fetch.';
    },
  },
});

const operatorReducer = operatorSlice.reducer;
export const {
  getOperators,
  getOperatorsSuccess,
  getOperatorsFailure,
  getOperator,
  getOperatorSuccess,
  getOperatorFailure,
  getOperatorsList,
  getOperatorsListSuccess,
  getOperatorsListFailure,
  createOperator,
  createOperatorSuccess,
  createOperatorFailure,
  updateOperator,
  updateOperatorSuccess,
  updateOperatorFailure,
  deleteOperator,
  deleteOperatorSuccess,
  deleteOperatorFailure,
  activateOperator,
  activateOperatorSuccess,
  activateOperatorFailure,
  setShouldRefetchOperators,
  clearOperatorError,
  getOperatorsNoChange
} = operatorSlice.actions;
export default operatorReducer;
